import { useEffect, useRef, useState } from "react";
import axiosInstance from "../../interceptors";
import WarningToast from "../../components/toasts/warning-toast";
import ErrorToast from "../../components/toasts/error-toast";
import SuccessToast from "../../components/toasts/success-toast";
import { getCountryCode, getPhoneNumber } from "../../utils/phoneNumberUtils";

const SEND_URL = "/common/base/newEntityRegistration/send-verification-code";
const VERIFY_URL = "/common/base/newEntityRegistration/verify-code";
const RESEND_COOLDOWN_SECONDS = 30;

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.55)",
  backdropFilter: "blur(2px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  padding: 16,
};

const cardStyle = {
  width: "100%",
  maxWidth: 460,
  background: "#fff",
  borderRadius: 14,
  boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
  padding: "22px 24px 18px",
  fontFamily: "'Inter', sans-serif",
  color: "#1E293B",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 6,
};

const titleStyle = { fontSize: 17, fontWeight: 700, margin: 0 };
const subtitleStyle = {
  fontSize: 12.5,
  color: "#64748B",
  margin: "0 0 16px",
  lineHeight: 1.5,
};

const choiceRowStyle = { display: "flex", gap: 10, marginBottom: 16 };
const choiceCardBase = {
  flex: 1,
  border: "1.5px solid #E2E8F0",
  borderRadius: 10,
  padding: "12px 12px",
  cursor: "pointer",
  background: "#F8FAFC",
  transition: "all 0.2s",
  textAlign: "left",
  fontFamily: "inherit",
};
const choiceCardActive = {
  border: "1.5px solid #7B5AC4",
  background: "#EEF2FF",
  boxShadow: "0 0 0 3px rgba(99,102,241,0.15)",
};

const codeInputStyle = {
  width: "100%",
  height: 46,
  border: "1.5px solid #E2E8F0",
  borderRadius: 10,
  padding: "0 14px",
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: 6,
  textAlign: "center",
  outline: "none",
  fontFamily: "inherit",
  background: "#F8FAFC",
  color: "#1E293B",
};

const primaryBtn = {
  height: 42,
  padding: "0 18px",
  borderRadius: 10,
  border: "none",
  background: "linear-gradient(135deg, #7B5AC4 0%, #8B5CF6 100%)",
  color: "#fff",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "inherit",
};

const secondaryBtn = {
  height: 42,
  padding: "0 16px",
  borderRadius: 10,
  border: "1.5px solid #E2E8F0",
  background: "#fff",
  color: "#475569",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "inherit",
};

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  marginTop: 14,
  paddingTop: 14,
  borderTop: "1px solid #EEF0F5",
};

const splitContact = (rawWhatsapp) => {
  const code = getCountryCode(rawWhatsapp);
  const number = getPhoneNumber(rawWhatsapp);
  return {
    phoneCode: code ? `+${code}` : "",
    whatsappNumber: number || "",
  };
};

const RegistrationVerificationModal = ({
  open,
  email,
  whatsappNumber,
  onCancel,
  onVerified,
}) => {
  const [channel, setChannel] = useState("EMAIL");
  const [stage, setStage] = useState("choose");
  const [verificationId, setVerificationId] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [expiresAt, setExpiresAt] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [resendIn, setResendIn] = useState(0);
  const tickerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStage("choose");
      setChannel(email ? "EMAIL" : "WHATSAPP");
      setVerificationId("");
      setCode("");
      setSending(false);
      setVerifying(false);
      setExpiresAt(0);
      setSecondsLeft(0);
      setResendIn(0);
    } else {
      setChannel(email ? "EMAIL" : "WHATSAPP");
    }
  }, [open, email]);

  useEffect(() => {
    if (stage !== "enter") return undefined;
    tickerRef.current = setInterval(() => {
      const now = Date.now();
      setSecondsLeft(Math.max(0, Math.floor((expiresAt - now) / 1000)));
      setResendIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(tickerRef.current);
  }, [stage, expiresAt]);

  if (!open) return null;

  const { phoneCode, whatsappNumber: waNumber } = splitContact(whatsappNumber);

  const contactLabel =
    channel === "EMAIL" ? email : `${phoneCode || ""} ${waNumber}`.trim();

  const requestSend = async () => {
    if (channel === "EMAIL" && !email) {
      return WarningToast("Email is missing on the primary contact");
    }
    if (channel === "WHATSAPP" && !waNumber) {
      return WarningToast("WhatsApp number is missing on the primary contact");
    }
    setSending(true);
    try {
      const body =
        channel === "EMAIL"
          ? { channel: "EMAIL", email }
          : { channel: "WHATSAPP", phoneCode, whatsappNumber: waNumber };
      const { data } = await axiosInstance.post(SEND_URL, body);
      if (!data?.success) {
        return WarningToast(data?.message || "Could not send verification code");
      }
      setVerificationId(data.verificationId);
      const lifetime = (data.expiresInSeconds || 600) * 1000;
      setExpiresAt(Date.now() + lifetime);
      setSecondsLeft(Math.floor(lifetime / 1000));
      setResendIn(RESEND_COOLDOWN_SECONDS);
      setCode("");
      setStage("enter");
      SuccessToast(`Verification code sent to ${contactLabel}`);
    } catch (err) {
      ErrorToast(err?.message || "Failed to send verification code");
    } finally {
      setSending(false);
    }
  };

  const requestVerify = async () => {
    if (!code || code.trim().length !== 6) {
      return WarningToast("Please enter the 6-digit code");
    }
    setVerifying(true);
    try {
      const { data } = await axiosInstance.post(VERIFY_URL, {
        verificationId,
        code: code.trim(),
      });
      if (data?.verified) {
        SuccessToast(data?.message || "Verified successfully");
        onVerified?.({ channel, contact: contactLabel });
        return;
      }
      if (data?.attemptsRemaining === 0) {
        WarningToast(data?.message || "Too many attempts. Request a new code.");
        setStage("choose");
        setVerificationId("");
      } else {
        WarningToast(
          data?.message ||
            `Invalid code${
              data?.attemptsRemaining
                ? ` — ${data.attemptsRemaining} attempts left`
                : ""
            }`,
        );
        setCode("");
      }
    } catch (err) {
      ErrorToast(err?.message || "Failed to verify code");
    } finally {
      setVerifying(false);
    }
  };

  const renderChoose = () => (
    <>
      <p style={subtitleStyle}>
        Choose where you'd like to receive your 6-digit verification code.
      </p>
      <div style={choiceRowStyle}>
        <button
          type="button"
          onClick={() => setChannel("EMAIL")}
          disabled={!email}
          style={{
            ...choiceCardBase,
            ...(channel === "EMAIL" ? choiceCardActive : {}),
            opacity: email ? 1 : 0.5,
            cursor: email ? "pointer" : "not-allowed",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
            Email
          </div>
          <div style={{ fontSize: 12, color: "#64748B", wordBreak: "break-all" }}>
            {email || "No email on file"}
          </div>
        </button>
        <button
          type="button"
          onClick={() => setChannel("WHATSAPP")}
          disabled={!waNumber}
          style={{
            ...choiceCardBase,
            ...(channel === "WHATSAPP" ? choiceCardActive : {}),
            opacity: waNumber ? 1 : 0.5,
            cursor: waNumber ? "pointer" : "not-allowed",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
            WhatsApp
          </div>
          <div style={{ fontSize: 12, color: "#64748B" }}>
            {waNumber ? `${phoneCode} ${waNumber}` : "No WhatsApp number on file"}
          </div>
        </button>
      </div>
      <div style={footerStyle}>
        <button type="button" style={secondaryBtn} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          style={{ ...primaryBtn, opacity: sending ? 0.7 : 1 }}
          onClick={requestSend}
          disabled={sending}
        >
          {sending ? "Sending..." : "Send Code"}
        </button>
      </div>
    </>
  );

  const fmt = (s) => {
    if (s <= 0) return "expired";
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  const renderEnter = () => (
    <>
      <p style={subtitleStyle}>
        We sent a 6-digit code to <strong>{contactLabel}</strong>. Enter it below
        to continue.
      </p>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={6}
        value={code}
        autoFocus
        onChange={(e) =>
          setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
        }
        placeholder="000000"
        style={codeInputStyle}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 10,
          fontSize: 12,
          color: "#64748B",
        }}
      >
        <span>
          {secondsLeft > 0
            ? `Code expires in ${fmt(secondsLeft)}`
            : "Code expired — request a new one"}
        </span>
        <button
          type="button"
          onClick={requestSend}
          disabled={resendIn > 0 || sending}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: resendIn > 0 ? "#94A3B8" : "#7B5AC4",
            fontWeight: 600,
            fontSize: 12,
            cursor: resendIn > 0 ? "not-allowed" : "pointer",
            fontFamily: "inherit",
          }}
        >
          {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
        </button>
      </div>
      <div style={footerStyle}>
        <button
          type="button"
          style={secondaryBtn}
          onClick={() => setStage("choose")}
        >
          Back
        </button>
        <button
          type="button"
          style={{ ...primaryBtn, opacity: verifying ? 0.7 : 1 }}
          onClick={requestVerify}
          disabled={verifying || secondsLeft === 0}
        >
          {verifying ? "Verifying..." : "Verify & Continue"}
        </button>
      </div>
    </>
  );

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      <div style={cardStyle}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>
            {stage === "choose" ? "Verify Your Contact" : "Enter Verification Code"}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              color: "#94A3B8",
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>
        {stage === "choose" ? renderChoose() : renderEnter()}
      </div>
    </div>
  );
};

export default RegistrationVerificationModal;
